import { useEffect, useState } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    FormHelperText
} from "@mui/material"
import { Terminal } from "../../infrastructure/models/terminal"
import { TYPES } from "../../presentation/models/types"
import { CreateFranchise } from "../../infrastructure/models/transport/request/createfranchise"
import { UpdateFranchise } from "../../infrastructure/models/transport/request/UpdateFranchise"
import { PaymentType } from "../models/enum/PaymentType"
import { COLORS } from "../../presentation/constants/colors"
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { PaymentProvider } from "../../infrastructure/models/common/paymentProvider"

const STATUS_OPTIONS = [TYPES.ACTIVE, TYPES.INACTIVE, "Maintenance"]

interface UpdateFranchiseFormProps {
    franchise: any
    onClose: () => void
    onSubmit: (data: UpdateFranchise) => void
}

const DEFAULT_SMS_TEMPLATES = {
    defaultUserLoginOtp: "",
    defaultCollectPin: "",
    defaultDropOffAcknowledgement: "",
    defaultSubscriptionPinUpdate: "",
    defaultResendCollectPin: ""
}

const DEFAULT_EMAIL_TEMPLATES = {
    defaultUserLoginOtp: "",
    defaultRequestCollectPin: "",
    vendorLoginWelcome: "",
    vendorSendPassword: "",
    supportUserSendWelcome: ""
}

const UpdateFranchiseForm: React.FC<UpdateFranchiseFormProps> = ({ franchise, onClose, onSubmit }) => {
    const [formData, setFormData] = useState<UpdateFranchise>({
        franchiseId: "",
        name: "",
        status: "",
        paymentInfo: {
            paymentBaseUrl: "",
            paymentUrl: "",
            merchantId: "",
            apiKey: "",
            businessUnitCode: "",
            statusNotifyUrl: "",
            frontendBackUrl: ""
        },
        terminalPaymentUpdates: [],
        paymentProvider: "" as PaymentProvider,
        smsTemplates: DEFAULT_SMS_TEMPLATES,
        emailTemplates: DEFAULT_EMAIL_TEMPLATES,
        excludeOrdersPercentage: undefined,
        paymentDeductionValue: undefined
    })

    const [terminalPaymentUpdates, setTerminalPaymentUpdates] = useState<
        { terminalId: string; paymentType: PaymentType }[]
    >([])

    useEffect(() => {
        if (franchise) {
            setFormData({
                franchiseId: franchise.franchiseId,
                name: franchise.name || "",
                status: franchise.status?.toLowerCase() || "",
                paymentInfo: {
                    paymentBaseUrl: franchise.paymentInfo?.paymentBaseUrl || "",
                    paymentUrl: franchise.paymentInfo?.paymentUrl || "",
                    merchantId: franchise.paymentInfo?.merchantId || "",
                    apiKey: franchise.paymentInfo?.apiKey || "",
                    businessUnitCode: franchise.paymentInfo?.businessUnitCode || "",
                    statusNotifyUrl: franchise.paymentInfo?.statusNotifyUrl || "",
                    frontendBackUrl: franchise.paymentInfo?.frontendBackUrl || ""
                },
                paymentProvider: franchise.paymentProvider || "",
                terminalPaymentUpdates: [],
                smsTemplates: {
                    ...DEFAULT_SMS_TEMPLATES,
                    ...(franchise.smsTemplates || {})
                },
                emailTemplates: {
                    ...DEFAULT_EMAIL_TEMPLATES,
                    ...(franchise.emailTemplates || {})
                },
                excludeOrdersPercentage: franchise.excludeOrdersPercentage,
                paymentDeductionValue: franchise.paymentDeductionValue
            })
        }
    }, [franchise])

    const handleChange = (field: keyof UpdateFranchise, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const handlePaymentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            paymentInfo: {
                ...prev.paymentInfo,
                [name]: value
            }
        }))
    }

    const handleTemplateChange = (
        type: "smsTemplates" | "emailTemplates",
        key: string,
        value: string
    ) => {
        setFormData(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [key]: value
            }
        }))
    }


    return (
        <Box sx={{ p: 3, border: "1px solid #ccc", borderRadius: "8px", backgroundColor: "#fff" }}>
            <Typography variant="h5" gutterBottom>Update Franchise</Typography>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        label="Franchise Name"
                        value={formData.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        fullWidth
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            label="Status"
                            value={formData.status}
                            onChange={(e) => handleChange("status", e.target.value)}
                        >
                            {STATUS_OPTIONS.map(status => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Terminal Payment Updates</Typography>
            {(franchise.terminals?.length ?? 0) > 0 ? (
                <>
                    {terminalPaymentUpdates.map((item, index) => (
                        <Grid
                            container
                            spacing={2}
                            key={index}
                            alignItems="center"
                            sx={{ mb: 1, p: 1, }}
                        >
                            <Grid size={{ xs: 6, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Terminal</InputLabel>
                                    <Select
                                        label="Terminal"
                                        value={item.terminalId}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            setTerminalPaymentUpdates((prev) =>
                                                prev.map((t, i) =>
                                                    i === index ? { ...t, terminalId: value } : t
                                                )
                                            )
                                        }}
                                    >
                                        {franchise.terminals.map((t: any) => (
                                            <MenuItem key={t.terminalId} value={t.terminalId}>
                                                {t.name || t.terminalId}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 4, sm: 4 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Type</InputLabel>
                                    <Select
                                        label="Payment Type"
                                        value={item.paymentType}
                                        onChange={(e) => {
                                            const value = e.target.value as PaymentType
                                            setTerminalPaymentUpdates((prev) =>
                                                prev.map((t, i) =>
                                                    i === index ? { ...t, paymentType: value } : t
                                                )
                                            )
                                        }}
                                    >
                                        {Object.values(PaymentType).map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 2, sm: 2 }}>
                                <Button
                                    onClick={() =>
                                        setTerminalPaymentUpdates((prev) =>
                                            prev.filter((_, i) => i !== index)
                                        )
                                    }
                                >
                                    <CancelOutlinedIcon sx={{
                                        width: 32,
                                        height: 32,
                                        color: COLORS.DARK_RED,
                                    }} />
                                </Button>
                            </Grid>
                        </Grid>
                    ))}

                    <Button
                        variant="outlined"
                        sx={{ mt: 2 }}
                        onClick={() => {
                            const allTerminalIds = franchise.terminals.map(
                                (t: any) => t.terminalId
                            )
                            const addedTerminalIds = terminalPaymentUpdates.map(
                                (t) => t.terminalId
                            )

                            // Prevent adding duplicates / overflow
                            if (terminalPaymentUpdates.length >= allTerminalIds.length) {
                                alert("All terminals have been added already.")
                                return
                            }

                            // Find next unadded terminal to prefill
                            const nextTerminal = allTerminalIds.find(
                                (id) => !addedTerminalIds.includes(id)
                            )

                            setTerminalPaymentUpdates((prev) => [
                                ...prev,
                                {
                                    terminalId: nextTerminal || "",
                                    paymentType: PaymentType.DURING_DROP_OFF,
                                },
                            ])
                        }}
                    >
                        <AddOutlinedIcon sx={{ width: 20, height: 20, mr: 1 }} /> Add Terminal
                    </Button>

                    {/* Optional: small helper text */}
                    {terminalPaymentUpdates.length >= franchise.terminals.length && (
                        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            All terminals have been added.
                        </Typography>
                    )}
                </>
            ) : (
                <Typography color="textSecondary">
                    No terminals available for this franchise.
                </Typography>
            )}

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Payment Info</Typography>
            <Grid container spacing={2}>
                {Object.entries(formData.paymentInfo).map(([key, value]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={key}>
                        <TextField
                            fullWidth
                            name={key}
                            label={key}
                            value={value}
                            onChange={handlePaymentChange}
                        />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Payment Provider</Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                        <InputLabel>Payment Provider</InputLabel>
                        <Select
                            label="Payment Provider"
                            value={formData.paymentProvider || ""}
                            onChange={(e) => handleChange("paymentProvider", e.target.value as PaymentProvider)}
                        >
                            {(Object.values(PaymentProvider) as string[]).map((p) => (
                                <MenuItem key={p} value={p}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Order Settings</Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth  >
                        <InputLabel id="exclude-orders-percentage-label">
                            Exclude Orders Percentage (%)
                        </InputLabel>

                        <Select
                            labelId="exclude-orders-percentage-label"
                            label="Exclude Orders Percentage (%)"
                            name="excludeOrdersPercentage"
                            value={formData.excludeOrdersPercentage ?? 0}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    excludeOrdersPercentage: Number(e.target.value),
                                }))
                            }
                        >
                            {Array.from({ length: 101 }, (_, i) => (
                                <MenuItem key={i} value={i}>
                                    {i}%
                                </MenuItem>
                            ))}
                        </Select>

                        <FormHelperText>
                            {"Select the percentage of highest-value orders to exclude (0–100%)."}
                        </FormHelperText>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        select
                        fullWidth
                        label="Payment Deduction (%)"
                        value={formData.paymentDeductionValue ?? ""}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                paymentDeductionValue: e.target.value === "" ? undefined : e.target.value,
                            }))
                        }
                        helperText="Revenue deduction % (1–100). Defaults to system setting if not set."
                    >
                        <MenuItem value="">Default (system setting)</MenuItem>
                        {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => (
                            <MenuItem key={n} value={String(n)}>{n}%</MenuItem>
                        ))}
                    </TextField>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>SMS Templates (Optional)</Typography>
            <Grid container spacing={2}>
                {Object.entries(formData.smsTemplates || {}).map(([key, value]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={key}>
                        <TextField
                            fullWidth
                            label={key}
                            value={value}
                            onChange={(e) => handleTemplateChange("smsTemplates", key, e.target.value)}
                        />
                    </Grid>
                ))}
            </Grid>

            <Typography variant="h6" sx={{ mt: 4 }} gutterBottom>Email Templates (Optional)</Typography>
            <Grid container spacing={2}>
                {Object.entries(formData.emailTemplates || {}).map(([key, value]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={key}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={key}
                            value={value}
                            onChange={(e) => handleTemplateChange("emailTemplates", key, e.target.value)}
                        />
                    </Grid>
                ))}
            </Grid>
            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={() => onSubmit({ ...formData, terminalPaymentUpdates })}
            >
                Save
            </Button>

        </Box>
    )
}

export default UpdateFranchiseForm